import "./transactions.css";
import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Card,
  Table,
  Tag,
  Modal,
  Form,
  DatePicker,
  Input,
  Select,
  Button,
  Radio,
  Drawer,
  Collapse,
  Space,
  Tooltip,
  Switch,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  HomeOutlined,
  DollarOutlined,
  EyeOutlined,
  CloudDownloadOutlined,
  UndoOutlined,
  CloseOutlined,
  FilterOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import {
  getTransactions,
  refundTransaction,
  downloadExcelReport,
  getBranches,
  getMyProfile,
  createTickets,
} from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { handleApiError, showAppError } from "../../../utils/errorHandler";
import { AppContext } from "../../../context/AppContextProvider";
import QuantityStepper from "../../../components/QuantityStepper";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import {
  TransactionType,
  TransactionStatus,
  TransactionOrderSource,
  transactionTypes,
  RefundType,
  UserType,
  OrderType,
  AddonName,
  ZatcaInvoiceStatus,
  ZatcaInvoiceSubtype,
  ZATCA_INVOICE_RETRY_MAX,
  zatcaDocumentTypes,
  zatcaInvoiceSubtypes,
  zatcaInvoiceStatuses,
  zatcaRefundReasons,
  zatcaResponseStatuses,
} from "../../../configs/constants";
import moment from "moment";
import { useTranslation } from "react-i18next";
import TransactionSlip from "../../../components/TransactionSlip/TransactionSlip";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import ReactToPrint from "react-to-print";
import { useNavigate } from "react-router-dom";
import InvoiceDetailDrawer from "../ZatcaInvoices/InvoiceDetailDrawer";

const getOption = (options, id) => options.find((item) => item.id === id);

const getZatcaValue = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value.value || value.id || value.name;
  return value;
};

const getOptionTag = (options, value, t) => {
  const id = getZatcaValue(value);
  const option = getOption(options, id);

  if (option) {
    return <Tag color={option.color}>{t(option.text)}</Tag>;
  }

  if (value?.label) {
    return <Tag>{value.label}</Tag>;
  }

  return id ? <Tag>{t(id)}</Tag> : null;
};

const isEnabledFlag = (value) => {
  return value === true || value === 1 || value === "1";
};

const isTicketItem = (item) => isEnabledFlag(item?.is_ticket);

const isTicketIssued = (item) => isEnabledFlag(item?.ticket_is_issued);

const getIssueableTicketItems = (items = []) => {
  if (!Array.isArray(items)) return [];

  return items.filter((item) => isTicketItem(item) && !isTicketIssued(item));
};

const Transactions = () => {
  const { authUser, appConfig, roundOff } = useContext(AppContext);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [transactionsTableData, setTransactionsTableData] = useState([]);
  const [paginationData, setPaginationData] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(10);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [transaction, setTransaction] = useState({});
  const [searchParams, setSearchParams] = useState([]);
  const [slipModalVisible, setSlipModalVisible] = useState(false);
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [refundType, setRefundType] = useState(RefundType.Full);
  const [zatcaRefundReason, setZatcaRefundReason] = useState(null);
  const [cart, setCart] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [branchesData, setBranchesData] = useState([]);
  const [searchFormRef] = Form.useForm();
  const transactionSlipRef = useRef();
  const { t } = useTranslation();
  const { RangePicker } = DatePicker;
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [productsData, setProductsData] = useState([]);
  const [isAllData, setIsAllData] = useState(false);
  const [modal, contextHolder] = Modal.useModal();
  const [isTicketIssuing, setIsTicketIssuing] = useState(false);

  const [invoiceDrawerOpen, setInvoiceDrawerOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  const activeAddons = authUser?.active_addons || [];

  const is_ticketing_addon_active = activeAddons.some(
    (addon) => addon.id && addon.name === AddonName.Ticketing,
  );

  const is_stock_management_active = activeAddons.some(
    (addon) => addon.id && addon.name === AddonName.Stock,
  );

  const is_zatca_e_invoicing_active = activeAddons.some(
    (addon) => addon.id && addon.name === AddonName.ZatcaEInvoicing,
  );

  let navigate = useNavigate();

  useEffect(() => {
    setIsDataLoading(true);
    getTransactions({
      ...searchParams,
      page: currentPage,
      per_page: perPageSize,
      is_all_data: isAllData,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setTransactionsTableData(response.data.data.transactions);
        setPaginationData(response.data.data.pagination);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [searchParams, currentPage, perPageSize, reloadTableData, isAllData, t]);

  useEffect(() => {
    let tax = 0;
    let total = 0;
    cart.forEach((product) => {
      const productSubTotal = product.price * product.refund_quantity;
      let productTax = 0;

      if ((product.is_taxable || product.tax) && product.taxes?.length) {
        product.taxes.forEach((tax) => {
          productTax += productSubTotal * (Number(tax.rate) / 100);
        });
      }

      tax += productTax;
      total += productSubTotal + productTax;
    });
    setTotalTax(roundOff(tax));
    setTotalAmount(roundOff(total));
  }, [cart, roundOff]);

  useEffect(() => {
    let categories = [
      { id: 0, name: t("all"), name_ar: t("all", { lng: "ar" }) },
    ];
    let products = [];
    getMyProfile()
      .then((response) => {
        let data = response.data || response.response.data;
        if (data.success === true) {
          data.data.user.categories.forEach((category) => {
            if (category.products.length > 0) {
              categories.push({
                id: category.id,
                name: category.name,
                name_ar: category.name_ar,
              });
              products = [...products, ...category.products];
            }
          });
          setProductsData(products);
        } else if (data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  }, [t]);

  useEffect(() => {
    if (
      transaction &&
      refundModalVisible &&
      transaction.items &&
      transaction.items.length > 0
    ) {
      const mergedA = transaction.items.map((itemA) => {
        const matchingItem = productsData.find(
          (itemB) => itemB.id === itemA.product_id,
        );

        const baseItem = {
          ...itemA,
          refund_quantity: itemA.refund_quantity || 0,
        };

        if (matchingItem) {
          return {
            ...baseItem,
            is_taxable: matchingItem.is_taxable ?? itemA.is_taxable,
          };
        }
        return baseItem;
      });

      setCart(mergedA);
    }
  }, [transaction, refundModalVisible, productsData, refundType]);

  useEffect(() => {
    setIsDataLoading(true);
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBranchesData(response.data.data.branches);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [t]);

  const cartReducer = (product, action) => {
    let updatedCart = JSON.parse(JSON.stringify(cart));
    let productId = product.id;
    let indexFound = updatedCart.findIndex(
      (product) => product.id === productId,
    );

    if (action === "inc") {
      updatedCart[indexFound].refund_quantity++;
    } else if (action === "dec") {
      updatedCart[indexFound].refund_quantity--;
    }

    setCart(updatedCart);
  };

  const showSlipModal = (transaction) => {
    setTransaction(transaction);
    setSlipModalVisible(true);
  };

  const hideSlipModal = () => {
    setSlipModalVisible(false);
    setTransaction({});
  };

  const openInvoiceDrawer = (record) => {
    if (!record?.id) return;

    setSelectedInvoiceId(record.id);
    setInvoiceDrawerOpen(true);
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "transactions",
      breadcrumbName: t("transactions"),
      icon: <DollarOutlined />,
    },
  ];

  const getTransactionsType = (type) => {
    if (type === TransactionType.Cash) {
      return <Tag color="green">{t("cash")}</Tag>;
    } else if (type === TransactionType.Credit) {
      return <Tag color="blue">{t("credit_card")}</Tag>;
    } else if (type === TransactionType.Multipayment) {
      return <Tag color="gold">{t("multi_payment")}</Tag>;
    }
  };

  const renderZatcaInvoiceInfo = (record) => {
    const zatca = record?.zatca;

    if (!zatca?.is_zatca_invoice) {
      return "-";
    }

    const status = getZatcaValue(zatca.status);
    const documentType = getZatcaValue(zatca.document_type);
    const invoiceSubtype = zatca.is_b2b
      ? ZatcaInvoiceSubtype.Standard
      : zatca.is_b2c
        ? ZatcaInvoiceSubtype.Simplified
        : getZatcaValue(zatca.invoice_subtype);

    const warningsCount = Number(zatca.warnings_count || 0);
    const errorsCount = Number(zatca.errors_count || 0);

    return (
      <Space direction="vertical" size={4} style={{ lineHeight: 1.2 }}>
        <Space size={4} wrap>
          {getOptionTag(zatcaInvoiceStatuses, zatca.status, t)}
          {getOptionTag(zatcaDocumentTypes, documentType, t)}
          {getOptionTag(zatcaInvoiceSubtypes, invoiceSubtype, t)}
          {getOptionTag(zatcaResponseStatuses, zatca.response_status, t)}
        </Space>

        {(warningsCount > 0 || errorsCount > 0) && (
          <Space size={4} wrap>
            {warningsCount > 0 && (
              <Tag color="gold">
                {warningsCount} {t("warnings")}
              </Tag>
            )}

            {errorsCount > 0 && (
              <Tag color="red">
                {errorsCount} {t("zatca_errors")}
              </Tag>
            )}
          </Space>
        )}

        {status === ZatcaInvoiceStatus.Failed &&
        zatca.next_retry_at &&
        (zatca.retry_count || 0) < ZATCA_INVOICE_RETRY_MAX ? (
          <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
            {t("zatca_invoice_retry_attempt", {
              minutes: Math.max(
                1,
                moment(zatca.next_retry_at).diff(moment(), "minutes"),
              ),
              n: zatca.retry_count || 0,
              max: ZATCA_INVOICE_RETRY_MAX,
            })}
          </span>
        ) : null}
      </Space>
    );
  };

  const isTakeawayInProgressPaidTransaction = (transactionRow) => {
    return (
      !transactionRow?.is_refunded &&
      !transactionRow?.reference_transaction &&
      transactionRow?.is_paid === true &&
      transactionRow?.order_type?.value === OrderType.TakeAway &&
      transactionRow?.status?.value === TransactionStatus.InProgress
    );
  };

  const showTakeawayRefundWarning = () => {
    modal.confirm({
      title: t("refund"),
      content: t("cancel_order_before_refund"),
      okText: t("ok"),
      cancelButtonProps: {
        style: { display: "none" },
      },
      centered: true,
    });
  };

  const isZatcaRefundReasonRequired = (transactionRow = transaction) => {
    const zatca = transactionRow?.zatca;

    return Boolean(
      is_zatca_e_invoicing_active &&
      (isEnabledFlag(zatca?.is_zatca_invoice) || zatca?.invoice_id),
    );
  };

  const firstPartCol = [
    {
      title: t("transaction_id"),
      dataIndex: "uid",
      render: (uid) => {
        return <p>{uid}</p>;
      },
    },
    {
      title: t("amount"),
      render: (record) => {
        return (
          <>
            {record.is_multipayment ? (
              record.multipayments.map((item, i) => {
                return (
                  <p key={i}>
                    {item.transaction_type}:{" "}
                    <CurrencyFormatter value={item.amount} />
                  </p>
                );
              })
            ) : (
              <p>
                <CurrencyFormatter value={record.amount_charged} />
              </p>
            )}
          </>
        );
      },
    },
    {
      title: t("taxes"),
      dataIndex: "tax",
      render: (tax) => {
        return (
          <p>
            <CurrencyFormatter value={tax} />
          </p>
        );
      },
    },
  ];

  const branchCol = [
    {
      title: t("branch"),
      dataIndex: "branch",
      render: (branch) => {
        return <p>{branch}</p>;
      },
    },
  ];

  const secondPartCol = [
    {
      title: t("type"),
      render: (record) => {
        return (
          <div className="app-flex-wrap-center">
            {getTransactionsType(record.type)}
            {Number(record.order_source?.value) ===
              TransactionOrderSource.Pos && (
              <img
                src="/icons/pos.svg"
                alt={t("pos")}
                title={t("pos")}
                className="app-order-meta-icon"
              />
            )}

            {Number(record.order_source?.value) ===
              TransactionOrderSource.QrOrder &&
              (record.is_drive_thru ? (
                <img
                  src="/icons/car.svg"
                  alt={t("drive_thru")}
                  title={t("drive_thru")}
                  className="app-order-meta-icon"
                />
              ) : (
                <img
                  src="/icons/qr.svg"
                  alt={t("qr")}
                  title={t("qr")}
                  className="app-order-meta-icon"
                />
              ))}

            {Number(record.order_source?.value) ===
              TransactionOrderSource.Kiosk && (
              <img
                src="/icons/kiosk.svg"
                alt={t("kiosk")}
                title={t("kiosk")}
                className="app-order-meta-icon"
              />
            )}
            {record.dining_table_name && (
              <>
                <br />
                <Tag color="blue">{record.dining_table_name}</Tag>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: t("status"),
      width: 155,
      render: (transaction) => {
        return (
          <p>
            {transaction.reference_transaction ? (
              <>
                <Tag color="error">{t("status_refunded")}</Tag>
                <div
                  style={{
                    fontSize: "12px",
                    lineHeight: 1.3,
                    marginTop: "5px",
                  }}
                >
                  {t("reference_no")}:{" "}
                  <strong>{transaction.reference_transaction.uid}</strong>
                </div>
              </>
            ) : (
              <Tag color="success">{t("status_paid")}</Tag>
            )}
          </p>
        );
      },
    },
    ...(is_zatca_e_invoicing_active
      ? [
          {
            title: t("zatca_status"),
            width: 190,
            render: (record) => renderZatcaInvoiceInfo(record),
          },
        ]
      : []),
    {
      title: `${t("created_at")} / ${t("created_by")}`,
      render: (record) => {
        return (
          <div style={{ fontSize: "13px", lineHeight: 1.3 }}>
            <div>
              {moment(record.created_at).format("MMM. D, YYYY hh:mm a")}
            </div>

            {record.user && (
              <Tag
                color="cyan"
                style={{
                  fontSize: "12px",
                  marginTop: "5px",
                }}
              >
                {record.user}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: t("action"),
      width: "150px",
      render: (record) => {
        return (
          <Space>
            {!record.is_refunded &&
              !record.reference_transaction &&
              authUser.can_refund_transaction && (
                <Button
                  type="link"
                  onClick={() =>
                    showRefundModal(JSON.parse(JSON.stringify(record)))
                  }
                  title={t("refund")}
                >
                  <UndoOutlined />
                </Button>
              )}

            <Button type="link" onClick={() => showSlipModal(record)}>
              <PrinterOutlined title={t("print_invoice")} />
            </Button>

            {is_zatca_e_invoicing_active && record?.zatca?.invoice_id && (
              <Button type="link" onClick={() => openInvoiceDrawer(record)}>
                <EyeOutlined />
              </Button>
            )}

            <Button
              type="link"
              target="_blank"
              href={
                process.env.REACT_APP_API_ROOT +
                "/companies/" +
                record.company_id +
                "/transactions/" +
                record.id +
                "/generate-slip-pdf"
              }
            >
              <CloudDownloadOutlined title={t("download_slip_pdf")} />
            </Button>
          </Space>
        );
      },
    },
  ];

  const handleSearch = (values) => {
    setDrawerVisible(false);
    let search_params = false;

    if (values.dates) {
      let startDateUtc = moment.utc(values.dates[0]).set({
        date: values.dates[0].date() - 1,
        month: values.dates[0].month(),
        year: values.dates[0].year(),
        hour: 19,
        minute: 0,
        second: 0,
        millisecond: 0,
      });

      let endDateUtc = moment.utc(values.dates[1]).set({
        date: values.dates[1].date(),
        month: values.dates[1].month(),
        year: values.dates[1].year(),
        hour: 18,
        minute: 59,
        second: 59,
        millisecond: 0,
      });

      search_params = {
        search_start_date: startDateUtc.format(),
        search_end_date: endDateUtc.format(),
      };
    }

    if (values.transaction_id) {
      search_params = {
        transaction_id: values.transaction_id,
        ...search_params,
      };
    }

    if (values.type) {
      search_params = {
        type: values.type,
        ...search_params,
      };
    }

    if (values.is_refunded === 0 || values.is_refunded) {
      search_params = {
        is_refunded: values.is_refunded,
        ...search_params,
      };
    }

    if (values.filter_by_branch) {
      search_params = {
        filter_by_branch: values.filter_by_branch,
        ...search_params,
      };
    }

    if (search_params) {
      setSearchParams(search_params);
    } else {
      setSearchParams([]);
    }
  };

  const handleReset = () => {
    setDrawerVisible(false);
    searchFormRef.resetFields();
    setSearchParams([]);
  };

  const showRefundModal = (transactionRow) => {
    if (isTakeawayInProgressPaidTransaction(transactionRow)) {
      showTakeawayRefundWarning();
      return;
    }

    setTransaction(transactionRow);
    setRefundModalVisible(true);
    setRefundType(RefundType.Full);
    setZatcaRefundReason(null);
  };

  const hideRefundModal = () => {
    setCart([]);
    setRefundModalVisible(false);
    setTransaction({});
    setZatcaRefundReason(null);
  };

  const handleTableChange = (pagination) => {
    setPaginationData(pagination);
    setCurrentPage(pagination.current);
    setPerPageSize(pagination.pageSize);
  };

  const handleRefund = () => {
    const requiresZatcaRefundReason = isZatcaRefundReasonRequired();

    if (requiresZatcaRefundReason && !zatcaRefundReason) {
      showAppError(t("select_zatca_refund_reason"));
      return;
    }

    const refundPayload = {
      transaction_id: transaction.id,
      refund_type: refundType,
      items: JSON.stringify(
        cart
          .filter((item) => item.refund_quantity > 0)
          .map((item) => ({
            id: item.product_id,
            qty: item.refund_quantity,
          })),
      ),
      ...(requiresZatcaRefundReason && {
        zatca_refund_reason: zatcaRefundReason,
      }),
    };

    refundTransaction(refundPayload)
      .then((response) => {
        let data = response?.data || response?.response?.data;

        if (data?.success === true) {
          NotificationWithIcon(
            "success",
            t("transaction_refunded"),
            t("transaction_refunded_successfully"),
          );
          setReloadTableData(!reloadTableData);
        } else if (data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        hideRefundModal();
      });
  };

  const handleDownloadReport = () => {
    downloadExcelReport().catch((error) => handleApiError(error, t));
  };

  const hasUnissuedTickets =
    getIssueableTicketItems(transaction?.items).length > 0;

  const shouldShowZatcaRefundReasonBox = isZatcaRefundReasonRequired();
  const isRefundAmountValid =
    refundType === RefundType.Full ||
    (refundType === RefundType.Partial && totalAmount > 0);
  const isRefundDisabled =
    !isRefundAmountValid ||
    (shouldShowZatcaRefundReasonBox && !zatcaRefundReason);

  const handlePrintTicket = () => {
    if (isTicketIssuing) return;

    const issueableTicketItems = getIssueableTicketItems(transaction?.items);

    if (!issueableTicketItems.length) {
      showAppError(t("no_unissued_ticket_found"));
      return;
    }

    const transactionObj = issueableTicketItems.map((item) => ({
      product_id: item.product_id ?? item.id,
      quantity: item.quantity,
      transaction_id: transaction.id,
      ...(is_stock_management_active &&
        item.expiry_date && { expiry_date: item.expiry_date }),
    }));

    setIsTicketIssuing(true);

    createTickets(transactionObj)
      .then((response) => {
        let data = response.data || response.response?.data;
        if (data?.success === true) {
          setTransaction((prevTransaction) => ({
            ...prevTransaction,
            items: prevTransaction?.items?.map((item) =>
              isTicketItem(item) ? { ...item, ticket_is_issued: 1 } : item,
            ),
          }));
          setReloadTableData(!reloadTableData);

          navigate("/print-ticket", {
            state: { tickets: data.data.tickets },
          });
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsTicketIssuing(false);
      });
  };

  return (
    <>
      {contextHolder}

      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("transactions")}
          ghost={false}
          extra={[
            authUser.type === UserType.BusinessOwner && (
              <Space
                key="toggle-all-data"
                size="small"
                style={{ alignSelf: "center", marginRight: 8 }}
              >
                <Tooltip
                  title={
                    isAllData
                      ? t("Showing_transactions_from_all_branches")
                      : t("Showing_transactions_from_my_branch")
                  }
                >
                  <Switch
                    checked={isAllData}
                    onChange={(checked) => {
                      setIsAllData(checked);
                      setCurrentPage(1);
                    }}
                    checkedChildren={t("all_data")}
                    unCheckedChildren={t("all_data")}
                  />
                </Tooltip>
              </Space>
            ),

            <Button
              type="primary"
              onClick={handleDownloadReport}
              key="download-report"
            >
              {t("download_report")}
            </Button>,
            <Button
              type="primary"
              onClick={() => {
                setDrawerVisible(true);
              }}
              key="filter"
            >
              <FilterOutlined />
            </Button>,
          ]}
        />
      </Card>

      <Card>
        {Object.keys(searchParams).length !== 0 && (
          <div style={{ marginBottom: "15px" }}>
            {searchParams.transaction_id && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.transaction_id;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["transaction_id"]);
                }}
              >
                {t("transaction_id")}: {searchParams.transaction_id}
              </Tag>
            )}

            {searchParams.search_start_date && searchParams.search_end_date && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.search_start_date;
                  delete searchParams.search_end_date;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["dates"]);
                }}
              >
                {t("date_range")}:{" "}
                {moment(searchParams.search_start_date).format("MMM. D, YYYY")}{" "}
                - {moment(searchParams.search_end_date).format("MMM. D, YYYY")}
              </Tag>
            )}

            {searchParams.filter_by_branch && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.filter_by_branch;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["filter_by_branch"]);
                }}
              >
                {t("branch")}: {searchParams.filter_by_branch}
              </Tag>
            )}

            {searchParams.type && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.type;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["type"]);
                }}
              >
                {t("type")}:{" "}
                {searchParams.type === TransactionType.Cash
                  ? t("cash")
                  : searchParams.type === TransactionType.Credit
                    ? t("credit_card")
                    : searchParams.type === TransactionType.Multipayment
                      ? t("multi_payment")
                      : ""}
              </Tag>
            )}

            {(searchParams.is_refunded === 0 ||
              searchParams.is_refunded === 1) && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.is_refunded;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["is_refunded"]);
                }}
              >
                {t("is_refunded")}:{" "}
                {searchParams.is_refunded ? t("yes") : t("no")}
              </Tag>
            )}
          </div>
        )}

        <Table
          columns={
            authUser.type === UserType.BusinessOwner
              ? [...firstPartCol, ...branchCol, ...secondPartCol]
              : [...firstPartCol, ...secondPartCol]
          }
          dataSource={transactionsTableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={{
            current: paginationData.current_page,
            pageSize: paginationData.per_page,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </Card>

      <Modal
        open={slipModalVisible}
        title={t("transaction_id") + " # " + transaction.uid}
        onCancel={hideSlipModal}
        footer={[
          <ReactToPrint
            trigger={() => (
              <Button key="print" type="primary">
                {t("print")}
              </Button>
            )}
            content={() => transactionSlipRef.current}
            documentTitle={transaction.uid}
            key="1"
          />,

          hasUnissuedTickets &&
            is_ticketing_addon_active &&
            !transaction?.is_refunded &&
            !transaction?.reference_transaction && (
              <Button
                type="primary"
                onClick={handlePrintTicket}
                loading={isTicketIssuing}
                disabled={isTicketIssuing}
                key="issue"
              >
                {t("issue_ticket")}
              </Button>
            ),
          <Button type="default" key="back" onClick={hideSlipModal}>
            {t("back")}
          </Button>,
        ]}
      >
        <TransactionSlip
          ref={transactionSlipRef}
          transaction={transaction}
          shop={{
            name: authUser.business_name,
            vat: authUser.vat,
            logo: authUser.logo,
            is_vat_exempt: authUser.is_vat_exempt,
          }}
        />
      </Modal>

      <Modal
        open={refundModalVisible}
        title={
          t("refund") + " " + t("transaction_id") + " # " + transaction.uid
        }
        onCancel={hideRefundModal}
        maskClosable={false}
        footer={[
          <Button
            type="primary"
            disabled={isRefundDisabled}
            onClick={handleRefund}
            key="primary"
          >
            {t("refund")}
          </Button>,
          <Button type="default" onClick={() => hideRefundModal()} key="cancel">
            {t("cancel")}
          </Button>,
        ]}
      >
        <p>{t("what_type_of_refund")}</p>
        <Radio.Group
          value={refundType}
          onChange={(e) => {
            setRefundType(e.target.value);
          }}
          buttonStyle="solid"
        >
          <Radio.Button value={RefundType.Full}>{t("full")}</Radio.Button>
          <Radio.Button value={RefundType.Partial}>
            {t("partial")}
          </Radio.Button>
        </Radio.Group>

        <br />
        <br />

        {shouldShowZatcaRefundReasonBox && (
          <div className="zatca-refund-reason-box">
            <div className="zatca-refund-reason-title">
              {t("zatca_refund_reason_required")}
            </div>
            <p>{t("zatca_refund_reason_helper")}</p>
            <Select
              value={zatcaRefundReason}
              onChange={setZatcaRefundReason}
              placeholder={t("select_zatca_refund_reason")}
              className="app-w-full"
              options={zatcaRefundReasons.map((reason) => ({
                label: t(reason.text),
                value: reason.id,
              }))}
            />
          </div>
        )}

        {refundType === RefundType.Full ? (
          <>
            {transaction.discount && (
              <h3 style={{ marginBottom: 0, paddingBottom: 0 }}>
                {t("discount")}: {transaction.discount.discount_percentage}%
              </h3>
            )}
            <h3 style={{ marginBottom: 0, paddingBottom: 0 }}>
              {t("refund_amount")}:{" "}
              <CurrencyFormatter value={transaction.amount_charged} />
            </h3>
          </>
        ) : (
          <>
            <Collapse
              defaultActiveKey={["1"]}
              items={
                cart &&
                cart.map((product) => {
                  let subTotal = product.price * product.refund_quantity;
                  let TaxPercentage = 0;

                  if (product.is_taxable === true) {
                    product.taxes.map((tax) => {
                      return (TaxPercentage = TaxPercentage + Number(tax.rate));
                    });
                    subTotal += subTotal * (TaxPercentage / 100);
                  }

                  return {
                    key: `product-${product.id}`,
                    label: (
                      <strong>
                        {`${
                          appConfig.direction === "rtl"
                            ? product.name
                            : product.name_en
                        }`}
                      </strong>
                    ),
                    extra: (
                      <span className="brand-color">
                        <CurrencyFormatter value={product.price} />
                      </span>
                    ),
                    children: (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          rowGap: "10px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            columnGap: "10px",
                            flexWrap: "wrap",
                          }}
                        >
                          {t("quantity")}:{" "}
                          <QuantityStepper
                            value={product.refund_quantity}
                            min={0}
                            max={product.quantity}
                            decreaseDisabled={product.refund_quantity <= 0}
                            increaseDisabled={
                              product.refund_quantity >= product.quantity
                            }
                            onDecrease={() => cartReducer(product, "dec")}
                            onIncrease={() => cartReducer(product, "inc")}
                          />
                        </div>

                        {product.is_taxable && (
                          <div className="app-flex-align-center-gap-10">
                            {t("tax")}:{" "}
                            {product.taxes.map((tax, i) => {
                              return (
                                <Tag color="orange" key={i}>
                                  {tax.title + " : " + tax.rate + "%"}
                                </Tag>
                              );
                            })}
                          </div>
                        )}

                        <div className="app-flex-align-center-gap-10">
                          {t("subtotal")}:{" "}
                          <span className="brand-color">
                            <CurrencyFormatter value={roundOff(subTotal)} />
                          </span>
                        </div>
                      </div>
                    ),
                  };
                })
              }
            />

            {transaction.discount && (
              <h3 style={{ marginBottom: 0, paddingBottom: 0 }}>
                {t("discount")}: {transaction.discount.discount_percentage}%
              </h3>
            )}
            <h3 style={{ marginBottom: 0, paddingBottom: 0 }}>
              {t("refund_amount")}: <CurrencyFormatter value={totalAmount} />
            </h3>
            <h4>
              {t("taxes")}: <CurrencyFormatter value={totalTax} />
            </h4>
          </>
        )}
      </Modal>

      <Drawer
        title={t("filters")}
        placement={appConfig.direction === "rtl" ? "left" : "right"}
        onClose={() => {
          setDrawerVisible(false);
        }}
        open={drawerVisible}
        closable={false}
        extra={
          <Button
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={() => {
              setDrawerVisible(false);
            }}
          />
        }
      >
        <Form
          name="search-transaction-form"
          layout="vertical"
          onFinish={handleSearch}
          form={searchFormRef}
        >
          <Form.Item name="transaction_id" label={t("transaction_id")}>
            <Input allowClear />
          </Form.Item>

          <Form.Item name="dates" label={t("date_range")}>
            <RangePicker
              allowClear
              format="DD/MM/YYYY"
              className="app-w-full"
            />
          </Form.Item>

          <Form.Item name="filter_by_branch" label={t("branch")}>
            <Select
              allowClear
              options={branchesData.map((branch) => {
                return {
                  label: branch.name,
                  value: branch.name,
                };
              })}
            ></Select>
          </Form.Item>

          <Form.Item name="type" label={t("type")}>
            <Select
              allowClear
              options={transactionTypes.map((type) => {
                return {
                  label: t(type.translationKey),
                  value: type.id,
                };
              })}
            ></Select>
          </Form.Item>

          <Form.Item name="is_refunded" label={t("is_refunded")}>
            <Select
              allowClear
              options={[
                {
                  label: t("yes"),
                  value: 1,
                },
                {
                  label: t("no"),
                  value: 0,
                },
              ]}
            ></Select>
          </Form.Item>

          <Form.Item>
            <Input
              type="submit"
              value={t("search")}
              className="app-search-submit"
            />
          </Form.Item>

          <Form.Item>
            <Input
              type="button"
              value={t("reset")}
              onClick={handleReset}
              style={{
                width: "80px",
              }}
            />
          </Form.Item>
        </Form>
      </Drawer>

      <InvoiceDetailDrawer
        open={invoiceDrawerOpen}
        transactionId={selectedInvoiceId}
        onClose={() => {
          setInvoiceDrawerOpen(false);
          setSelectedInvoiceId(null);
        }}
        onRetried={() => setReloadTableData(!reloadTableData)}
      />
    </>
  );
};

export default Transactions;
